/* Vectress web demo: the same engine the simulations run on, wired to the stage from the motion study. */
import {
  BLACK, LAYOUT, SHAPES, WHITE, bodyOf, cellNameOf, countAlive, countLost, describeEvents, legalMoves, newGame, parseCell, pass, playLegal,
} from '../../../packages/engine/src/index.ts';
import type { Game, LegalMove, ShapeId, Side } from '../../../packages/engine/src/index.ts';
import { greedyPlayer, makeRng } from '../../../packages/sim/src/players.ts';
import { previewsFor, sideName, summarise } from './preview.ts';
import type { Preview } from './preview.ts';
import { Stage, renderPieceIcon } from './stage.ts';
import type { View } from './stage.ts';

const rnd = makeRng(Date.now() & 0x7fffffff);
const $ = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;

type Mode = 'bot' | 'hotseat';

interface State {
  game: Game;
  history: Game[];
  selected: number | null;
  solo: boolean;
  moves: LegalMove[];
  mode: Mode;
  busy: boolean;
  /** the stage is acting out a move, and whose move it is (the board stays turned for them until it settles) */
  animating: boolean;
  moverSide: Side;
}

const state: State = { game: newGame(), history: [], selected: null, solo: false, moves: [], mode: 'bot', busy: false, animating: false, moverSide: WHITE };

const stage = new Stage($('stage-canvas') as HTMLCanvasElement, { onCell: cell => onCell(cell) });

const statusEl = $('status');
const detailEl = $('detail');
const logEl = $('log');
const soloEl = $('solo');
const scoreEl = $('score');

function start(mode: Mode = state.mode): void {
  state.game = newGame(LAYOUT);
  state.history = [];
  state.selected = null;
  state.solo = false;
  state.mode = mode;
  state.busy = false;
  state.moves = legalMoves(state.game);
  logEl.replaceChildren();
  stage.setPosition(state.game.pos);
  render();
}

/** In two-player play the board turns between turns, which is the pole rule made visible. */
function render(): void {
  const g = state.game;
  const viewer = state.animating ? state.moverSide : g.pos.toMove;
  stage.setFlipped(state.mode === 'hotseat' && viewer === BLACK && !g.result);
  const previews = state.selected === null ? [] : previewsFor(state.moves, state.selected, state.solo, g.pos.toMove);
  stage.setMarks(state.selected, previews);
  soloEl.hidden = state.selected === null || bodyOf(g.pos, state.selected).length < 2;
  for (const btn of soloEl.querySelectorAll('button')) btn.classList.toggle('on', (btn.dataset.solo === '1') === state.solo);

  scoreEl.innerHTML = `<span class="pill white"><i></i>White ${countAlive(g.pos, WHITE)} on, ${countLost(g.pos, WHITE)} lost</span>`
    + `<span class="pill black"><i></i>Black ${countAlive(g.pos, BLACK)} on, ${countLost(g.pos, BLACK)} lost</span>`;

  if (g.result) {
    const { winner, reason } = g.result;
    statusEl.textContent = winner === null ? 'Draw' : `${sideName(winner)} wins`;
    detailEl.textContent = REASON[reason];
    statusEl.className = 'status over';
    return;
  }
  statusEl.className = `status ${g.pos.toMove === WHITE ? 'white' : 'black'}`;
  statusEl.textContent = state.animating ? 'Resolving' : state.busy ? 'Bot thinking'
    : state.mode === 'bot' ? 'Your move' : `${sideName(g.pos.toMove)} to move`;
  detailEl.textContent = state.busy ? ''
    : state.selected === null ? `${state.moves.length} legal moves. Tap a piece to see what each one does.`
      : previews.length ? 'Tap a marker to play it.' : 'That piece has nowhere to go this way.';
}

const REASON: Record<string, string> = {
  core: 'The Core was knocked off the board.',
  material: 'Five pieces lost.',
  threefold: 'Same position three times: the side with more pieces takes it.',
  cap: 'Move limit reached: the side with more pieces takes it.',
  passes: 'Two passes in a row.',
};

function onCell(cell: number): void {
  const g = state.game;
  if (state.busy || g.result) return;
  if (state.mode === 'bot' && g.pos.toMove === BLACK) return;

  const chosen = state.selected === null ? null
    : previewsFor(state.moves, state.selected, state.solo, g.pos.toMove).find(p => p.cell === cell);
  if (chosen) { void commit(chosen); return; }

  const piece = g.pos.board[cell];
  if (piece >= 0 && g.pos.owner[piece] === g.pos.toMove) {
    state.selected = state.selected === piece ? null : piece;
    state.solo = false;
  } else {
    state.selected = null;
  }
  render();
}

/** Play the move and let the stage act it out from the event log. */
async function commit(p: Preview): Promise<void> {
  const before = state.game;
  state.busy = true;
  state.selected = null;
  stage.setMarks(null, []);
  const next = playLegal(before, p.move);
  state.history.push(before);
  state.game = next;
  state.moves = [];
  logMove(before, p);
  state.animating = true;
  state.moverSide = before.pos.toMove;
  render();

  await stage.play(before.pos, p.move.events);
  stage.setPosition(next.pos);
  state.animating = false;

  state.moves = legalMoves(state.game);
  state.busy = false;
  render();

  if (!state.game.result && !state.moves.length) {
    state.game = pass(state.game);
    state.moves = legalMoves(state.game);
    note(`${sideName(before.pos.toMove === WHITE ? BLACK : WHITE)} has no legal move and passes.`);
    render();
  }
  if (state.mode === 'bot' && !state.game.result && state.game.pos.toMove === BLACK) void botMove();
}

async function botMove(): Promise<void> {
  state.busy = true;
  render();
  await sleep(500);
  const moves = legalMoves(state.game);
  if (!moves.length) { state.busy = false; render(); return; }
  const lm = greedyPlayer(state.game, moves, rnd);
  const { kind, alsoLoses, text } = summarise(lm.pos, lm.events, BLACK);
  const first = lm.events[0];
  state.busy = false;
  await commit({ move: lm, cell: first.kind === 'move' ? first.to : 0, kind, alsoLoses, text });
}

function logMove(before: Game, p: Preview): void {
  const mv = p.move.move;
  const shape = SHAPES[before.pos.shape[mv.piece]].name;
  const from = cellNameOf(before.pos.row[mv.piece] * 9 + before.pos.col[mv.piece]);
  const row = document.createElement('li');
  row.className = `entry ${before.pos.toMove === WHITE ? 'white' : 'black'} c-${p.kind}`;
  row.innerHTML = `<span class="t">${before.ply + 1}</span><span><b>${sideName(before.pos.toMove)} ${shape}</b> ${from} to ${cellNameOf(p.cell)}${mv.solo ? ', pulled free' : ''}`
    + `<em>${p.text}</em></span>`;
  row.title = describeEvents(before.pos, p.move.pos, p.move.events).join('\n');
  logEl.prepend(row);
  while (logEl.children.length > 40) logEl.lastElementChild?.remove();
}

function note(text: string): void {
  const row = document.createElement('li');
  row.className = 'entry note';
  row.innerHTML = `<span class="t"></span><span>${text}</span>`;
  logEl.prepend(row);
}

const sleep = (ms: number): Promise<void> => new Promise(r => { setTimeout(r, ms); });

/* ---------- controls ---------- */
$('new').addEventListener('click', () => start());
$('undo').addEventListener('click', () => {
  if (state.busy) return;
  let back = state.history.pop();
  if (state.mode === 'bot' && back && back.pos.toMove === BLACK) back = state.history.pop() ?? back;
  if (!back) return;
  state.game = back;
  state.selected = null;
  state.moves = legalMoves(back);
  stage.setPosition(back.pos);
  render();
});
function segment(id: string, onPick: (v: string) => void): void {
  for (const btn of document.querySelectorAll<HTMLButtonElement>(`#${id} button`)) {
    btn.addEventListener('click', () => {
      for (const b of document.querySelectorAll(`#${id} button`)) b.classList.toggle('on', b === btn);
      onPick(btn.dataset.v ?? '');
    });
  }
}
segment('mode', v => start(v as Mode));
segment('view', v => { stage.setView(v as View); document.querySelector('.stage')?.classList.toggle('top', v === 'top'); });
segment('speed', v => stage.setSpeed(Number(v)));
for (const btn of soloEl.querySelectorAll<HTMLButtonElement>('button')) {
  btn.addEventListener('click', () => { state.solo = btn.dataset.solo === '1'; render(); });
}

/* piece icons for the legend, drawn by the same renderer as the board */
for (const canvas of document.querySelectorAll<HTMLCanvasElement>('canvas[data-glyph]')) {
  renderPieceIcon(canvas, Number(canvas.dataset.glyph) as ShapeId, WHITE);
}

/* a hook for the visual smoke test: tap a cell by name */
(window as unknown as { __vectress: { tap: (cell: string) => void; busy: () => boolean } }).__vectress = {
  tap: (cell: string) => { const [r, c] = parseCell(cell); onCell(r * 9 + c); },
  busy: () => state.busy,
};

start();
